import {
  Flex,
  Box,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  Icon,
  Badge,
  IconButton,
  Select,
  Button,
  useDisclosure,
} from '@chakra-ui/react';
import * as React from 'react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

// Custom components
import Card from 'components/card/Card';
import Menu from 'components/menu/MainMenu';
import {
  MdCheckCircle,
  MdDelete,
  MdEdit,
  MdOutlineError,
  MdPending,
  MdSend,
  MdVisibility,
} from 'react-icons/md';
import { SearchBar } from 'components/navbar/searchBar/SearchBar';
import Pagination from 'components/pagination';
import { useNavigate } from 'react-router-dom';
import TeamMemberModal from './TeamMemberModal';
import FilterBar from 'components/filter-bar';
import InviteTeamMemberModal from './InviteTeamMemberModal';
import AssignLocationModal from './AssignLocationModal';
import ErrorMessage from 'components/error-message';
import { createAPIEndPoint } from 'config/api/api';
import toast from 'react-hot-toast';
import { toProperCase } from 'utils/formatting';
import DeleteModal from 'components/deleteModal/DeleteModal';
import AssignLocationModalV2 from './AssignLocationModalV2';
import moment from 'moment';

// Column Helper
const columnHelper = createColumnHelper();

// TeamTable Component
export default function TeamTable(props) {
  const { tableData, loading, fetchAgain } = props;
  const navigate = useNavigate();
  const [sorting, setSorting] = React.useState([]);
  const [type, setType] = React.useState('add');
  const [isDeleteModalOpen, setDeleteModalOpen] = React.useState(false);
  const [selectedItem, setSelectedItem] = React.useState()
  console.log(" TeamTable ~ selectedItem:", selectedItem)
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  // const primaryTextColor = useColorModeValue('brand.600', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const [isInviteOpen, setIsInviteOpen] = React.useState(false);
  const [isAssignOpen, setIsAssignOpen] = React.useState(false);

  const [selectedId, setSelectedId] = React.useState('');

  const handleCloseDeleteModal = () => setDeleteModalOpen(false);

  const handleDeleteItem = async () => {
    try {
      const response = await createAPIEndPoint('clinic_team/').delete(selectedItem.id);
      fetchAgain(true);
      toast.success('Team member deleted successfully!');
      handleCloseDeleteModal(); // ✅ Close the modal after successful delete
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Error deleting team member');
    }
  };



  const resendInvitation = async (memeberId) => {
    try {
      const payload = {};

      await createAPIEndPoint(`team/resend_invitation/${memeberId}`).create(payload);

      toast.success('Invitation resend successfully!');
    } catch (error) {
      toast.error(
        error?.response?.data?.error ||
        'Failed to accept invitation. Please try again.',
      );
    }
  };


  // Columns definition
  const columns = [
    columnHelper.accessor((row, index) => index + 1, {
      id: '#',
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          #
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="700">
          {info.getValue()}
        </Text>
      ),
    }),
    columnHelper.accessor((row) => `${row.first_name} ${row.last_name}`, {
      id: 'name',
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          NAME
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="700">
          {toProperCase(info.getValue()) || 'N/A'}
        </Text>
      ),
    }),

    columnHelper.accessor('email', {
      id: 'email',
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          EMAIL
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} textTransform="none" fontSize="sm" fontWeight="700">
          {info.getValue()}
        </Text>
      ),
    }),
    // columnHelper.accessor('phone', {
    //   id: 'phone',
    //   header: () => (
    //     <Text fontSize="12px" color="gray.400" fontWeight="bold">
    //       Phone
    //     </Text>
    //   ),
    //   cell: (info) => (
    //     <Text color={textColor} textTransform="none" fontSize="sm" fontWeight="700">
    //       {info.getValue()}
    //     </Text>
    //   ),
    // }),
    columnHelper.accessor('status', {
      id: 'status',
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          REQUEST STATUS
        </Text>
      ),
      cell: (info) => (
        <Flex align="center">
          <Icon
            w="24px"
            h="24px"
            me="5px"
            color={
              info.getValue()?.toLowerCase() === 'accepted'
                ? 'green.500'
                : info.getValue()?.toLowerCase() === 'Pending'
                  ? 'yellow.500'
                  : info.getValue()?.toLowerCase() === 'NA'
                    ? 'orange.500'
                    : null
            }
            as={
              info.getValue()?.toLowerCase() === 'accepted'
                ? MdCheckCircle
                : info.getValue()?.toLowerCase() === 'pending'
                  ? MdPending
                  : info.getValue()?.toLowerCase() === 'na'
                    ? MdOutlineError
                    : null
            }
          />
          <Text
            textTransform="capitalize"
            color={textColor}
            fontSize="sm"
            fontWeight="700"
          >
            {info.getValue()}
          </Text>
        </Flex>
      ),
    }),
    columnHelper.accessor('clinic_role', {
      id: 'clinic_role',
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          ROLE
        </Text>
      ),
      cell: (info) => {
        const role = info.getValue();
        const roleColors = {
          superadmin: 'purple',
          admin: 'red',
          lab: 'blue',
          staff: 'green',
          manager: 'orange',
          Billing: 'pink',
          default: "gray.500",
        };

        return (
          <Badge
            colorScheme={roleColors[role] || 'gray'}
            px={2}
            py={1.5}
            borderRadius="md"
            textAlign="center"
            minWidth="60px"
          >
            <Text fontSize="sm" textTransform="capitalize" fontWeight="700">
              {role === "superadmin" ? "Super admin" : role}
            </Text>
          </Badge>
        );
      },
    }),
    columnHelper.accessor('userStatus', {
      id: 'userStatus',
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          USER STATUS
        </Text>
      ),
      cell: (info) => {
        const role = info.getValue();
        const roleColors = {
          active: 'green',
          inactive: 'red',
        };

        return (
          <Badge
            colorScheme={roleColors[role] || 'gray'}
            px={2}
            py={1.5}
            borderRadius="md"
            textAlign="center"
            minWidth="72px"
          >
            <Text fontSize="sm" textTransform="capitalize" fontWeight="700">
              {role}
            </Text>
          </Badge>
        );
      },
    }),
    columnHelper.accessor('created_at', {
      id: 'created_at',
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          Added at
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="700">
          {info.getValue() ? moment(info.getValue()).format('MM/DD/YYYY') : 'N/A'}
        </Text>
      ),
    }),
    columnHelper.accessor('action', {
      id: 'action',
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          ACTION
        </Text>
      ),
      cell: (info) => {
        const handleViewClick = () => {
          const itemId = info.row.original.id; // Get the id from row data
          console.log('Navigating to details with ID:', itemId);

          navigate(`details/${itemId}`, { state: { id: itemId } }); // Pass ID in state
        };

        const handleEditClick = (e) => {
          const itemId = info.row.original.id; // Get the id from row data

          setSelectedId(itemId);
          onOpen();
          setType('edit');
        };

        const handleOpenDeleteModal = () => {
          setSelectedItem(info.row.original)
          setDeleteModalOpen(true);
        }




        const isPending = info.row.original.status.toLowerCase() === "pending";
        console.log(" TeamTable ~ isPending:", isPending)
        return (
          <Box display="flex">
            {isPending &&
              <IconButton
                icon={<MdSend style={{ marginLeft: '-1px', transform: "rotate(-45deg)", position: "relative", right: "-1px" }} />}
                aria-label="resend Invitation"
                colorScheme="teal"
                size="sm"
                mr="6px"
                onClick={() => resendInvitation(info.row.original.id)}
              />
            }
            <IconButton
              icon={<MdEdit style={{ marginLeft: '-1px' }} />}
              aria-label="Edit Details"
              colorScheme="brand"
              size="sm"
              mr="6px"
              onClick={handleEditClick}
            />
            <IconButton
              icon={<MdVisibility style={{ marginLeft: '-1px' }} />}
              aria-label="View Details"
              colorScheme="blackAlpha"
              size="sm"
              mr="6px"
              onClick={handleViewClick}
            />
            <IconButton
              icon={<MdDelete style={{ marginLeft: '-1px' }} />}
              aria-label="Delete"
              colorScheme="red"
              size="sm"
              onClick={handleOpenDeleteModal}
            />
          </Box>
        );
      },
    }),
  ];

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [data, setData] = React.useState([]);

  const table = useReactTable({
    data: data,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: true,
  });

  const [filters, setFilters] = React.useState({
    search: '',
    status: '',
    clinic_role: '',
    location: '',
  });


  const [searchTerm, setSearchTerm] = React.useState('');
  const [currentPage, setCurrentPage] = React.useState(1);
  const [itemsPerPage, setItemsPerPage] = React.useState(10);

  const handleFilterChange = (newFilters) => {
    console.log("Filters updated:", newFilters);
    setFilters(newFilters);
    setCurrentPage(1);

    let filteredData = [...tableData];

    filteredData = filteredData.filter((item) => {
      const fullName = `${item.first_name || ""} ${item.last_name || ""}`.trim().toLowerCase();
      const email = (item.email || "").toLowerCase();
      const searchValue = newFilters.search?.trim().toLowerCase() || "";

      // const matchesSearch =
      //   !searchValue || fullName.includes(searchValue) || email.includes(searchValue);

      const matchesSearch =
        !searchValue ||
        searchValue
          .split(" ")
          .filter(Boolean)
          .every((term) =>
            fullName.includes(term) || email.includes(term)
          );

      const matchesStatus =
        !newFilters.status ||
        (item.status ? item.status.trim().toLowerCase() : "") === newFilters.status.trim().toLowerCase();

      const matchesRole =
        !newFilters.clinic_role ||
        (item.clinic_role ? item.clinic_role.trim().toLowerCase() : "") === newFilters.clinic_role.trim().toLowerCase();

      const matchesLocation =
        !newFilters.location ||
        (item.location ? item.location.trim().toLowerCase() : "") === newFilters.location.trim().toLowerCase();

      return matchesSearch && matchesStatus && matchesRole && matchesLocation;
    });


    setData([...filteredData]); // Ensure reactivity
  };


  React.useEffect(() => {
    handleFilterChange({ ...filters, search: searchTerm });
  }, [searchTerm]);



  React.useEffect(() => {
    if (tableData) {
      setData([...tableData]); // Creates a fresh copy
    }
  }, [tableData]);


  const itemsPerPageOptions = [10, 20, 30, 50];

  const totalPages = Math.ceil(data.length / itemsPerPage);

  const paginatedData = data.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const handleInviteClick = () => {
    setIsInviteOpen(true);
  };

  const handleAssignClick = () => {
    setIsAssignOpen(true);
  };
  return (
    <>
      <Card flexDirection="column" w="100%" px="0px">
        <Flex
          px={{ base: '16px', md: '25px' }}
          mb="8px"
          justify="space-between"
          align="center"
          direction={{ base: 'column', md: 'row' }}
          flexWrap="wrap"
          gap={{ base: 4, md: 4 }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent={{ base: 'center', md: 'space-between' }}
            w="100%"
            direction={{ base: 'column', md: 'row' }}
            flexWrap="wrap"
            gap={{ base: 4, md: 2 }}
          >
            <Text
              color={textColor}
              fontSize="24px"
              fontWeight="700"
              lineHeight="100%"
              mb={{ base: '8px', md: '0px' }}
            >
              Team Members
            </Text>

            <Box
              w={{ base: '100%', md: 'auto' }}
              display="flex"
              justifyContent={{ base: 'center', md: 'flex-end' }}
              alignItems="center"
              flexDirection={{ base: 'column', md: "row" }}
              ml="auto"
              gap={2}
            // mr={{ md: 5 }}
            >
              <SearchBar
                width="100%"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  handleFilterChange({ ...filters, search: e.target.value });
                }}
              />

              <Button
                w={{ base: '100%', md: 'fit-content' }}
                minW="140px"
                variant="brand"
                fontWeight="500"
                onClick={() => {
                  onOpen();
                  setType('add');
                }}
                minH={"36px"}
                borderRadius="16px"
                mb={0}
                size="sm"
              >
                Add Team Member
              </Button>

            </Box>
          </Box>
          <Box
            ml="auto"
            display="flex"
            justify="center"
            alignItems="flex-end"
            w="fit"
            direction={{ base: 'column', md: 'row' }}
            flexWrap="wrap"
            gap={{ base: 4, md: 2 }}
          >
            <Box w={{ base: '100%', md: 'auto' }}>
              {/* <Button
                w={{ base: '100%', md: 'fit-content' }}
                minW="140px"
                variant="brand"
                fontWeight="500"
                onClick={() => {
                  onOpen();
                  setType('add');
                }}
                mr="8px"
                size="sm"
                borderRadius="12px"
              >
                Add Team Member
              </Button> */}
            </Box>
            {/* <Box w={{ base: '100%', md: 'auto' }}>
              <Button
                w={{ base: '100%', md: 'fit-content' }}
                variant="brand"
                fontWeight="500"
                onClick={handleInviteClick}
                mr="8px"
                size="sm"
                borderRadius="12px"
              >
                Invite Team Member
              </Button>
            </Box> */}
            {/* <Box w={{ base: '100%', md: 'auto' }}>
              <Button
                w={{ base: '100%', md: 'fit-content' }}
                variant="brand"
                fontWeight="500"
                onClick={handleAssignClick}
                mr="8px"
                size="sm"
                borderRadius="12px"
              >
                Assign Location
              </Button>
            </Box> */}
            {/* <Box w={{ base: '100%', md: 'auto' }}>
              <Button
                w={{ base: '100%', md: 'fit-content' }}
                colorScheme="teal"
                fontWeight="500"
                mr="8px"
                size="sm"
                borderRadius="12px"
                onClick={() => navigate('logs')}
              >
                Team Logs
              </Button>
            </Box> */}
          </Box>
        </Flex>

        <Box px={{ base: '16px', md: '25px' }}>
          <FilterBar onFilterChange={handleFilterChange} />{' '}
        </Box>
        <Flex
          px={{ base: '16px', md: '25px' }}
          mb="8px"
          justify="space-between"
          align="center"
          direction={{ base: 'column', md: 'row' }}
          gap={{ base: 4, md: 0 }}
        >
          <Box w={{ base: '100%', md: 'auto' }}>
            <Select
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
              width={{ base: '100%', md: 'fit-content' }}
              borderRadius="16px"
            >
              {itemsPerPageOptions.map((option) => (
                <option key={option} value={option}>
                  {option} per page
                </option>
              ))}
            </Select>
          </Box>

          <Box
            w={{ base: '100%', md: 'auto' }}
            display="flex"
            justifyContent={{ base: 'center', md: 'flex-end' }}
          >
            <Pagination
              totalPages={totalPages}
              initialPage={currentPage}
              onPageChange={handlePageChange}
            />{' '}
          </Box>
        </Flex>

        <Box overflowX="auto">
          <Table variant="striped" color="gray.500" mb="24px" mt="12px">
            <Thead>
              {table.getHeaderGroups()?.map((headerGroup) => (
                <Tr key={headerGroup.id}>
                  {headerGroup.headers?.map((header) => (
                    <Th
                      key={header.id}
                      colSpan={header.colSpan}
                      pe="10px"
                      borderColor={borderColor}
                      cursor="pointer"
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      <Flex justifyContent="space-between" align="center">
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                      </Flex>
                    </Th>
                  ))}
                </Tr>
              ))}
            </Thead>
            <Tbody>
              {paginatedData.map((row, index) => (
                <Tr key={index}>
                  {table
                    .getRowModel()
                    .rows.find((r) => r.original === row) // Ensure correct row mapping
                    ?.getVisibleCells()
                    ?.map((cell) => (
                      <Td
                        key={cell.id}
                        fontSize="14px"
                        borderColor="transparent"
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </Td>
                    ))}
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>

        {!loading &&
          data.length === 0 &&
          (filters && Object.keys(filters).length > 0 ? (
            <ErrorMessage source="results" />
          ) : (
            <ErrorMessage source="team" />
          ))}
      </Card>
      <TeamMemberModal
        isOpen={isOpen}
        onClose={onClose}
        type={type}
        memeberId={selectedId}
        fetchAgain={fetchAgain}
      />
      <InviteTeamMemberModal
        isOpen={isInviteOpen}
        onClose={() => setIsInviteOpen(false)}
      />
      <AssignLocationModalV2
        isOpen={isAssignOpen}
        onClose={() => setIsAssignOpen(false)}
      />
      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        onDelete={handleDeleteItem}
        selectedItem={`${selectedItem?.first_name} ${selectedItem?.last_name}`} />
    </>
  );
}
